import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Package, Scan, CheckCircle, AlertCircle, ChevronRight, Trophy, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FGPickerGuidance = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const slipNumber = searchParams.get('slip');
  const scanInputRef = useRef(null);

  const [slip, setSlip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffId, setStaffId] = useState('');
  const [staffValidated, setStaffValidated] = useState(false);
  const [staffName, setStaffName] = useState('');
  
  const [currentItem, setCurrentItem] = useState(null);
  const [itemIndex, setItemIndex] = useState(-1);
  const [bins, setBins] = useState([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [scannedCode, setScannedCode] = useState('');
  const [progress, setProgress] = useState({ completed_items: 0, total_items: 0, percentage: 0 });
  const [completed, setCompleted] = useState(false);
  
  // Skip/Partial dialog
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipRemarks, setSkipRemarks] = useState('');

  useEffect(() => {
    if (slipNumber) {
      fetchSlipDetails();
    } else {
      toast.error('No slip number provided');
      navigate('/wms/fg-pick-slips');
    }
  }, [slipNumber]);

  const fetchSlipDetails = async () => {
    try {
      setLoading(true);
      // Use query parameter endpoint to handle slashes in slip numbers
      const response = await apiClient.get(`/wms/fg/pick-slip-detail?slip_number=${encodeURIComponent(slipNumber)}`);
      setSlip(response.data);
      
      if (response.data.status === 'COMPLETED' || response.data.status === 'PARTIAL' || response.data.status === 'DC_LINKED') {
        setCompleted(true);
      } else if (response.data.status === 'IN_PROGRESS') {
        setStaffValidated(true);
        setStaffId(response.data.picker_staff_id || '');
        setStaffName(response.data.picker_name || '');
        fetchNextItem();
      }
    } catch (error) {
      toast.error('Failed to fetch slip details');
      navigate('/wms/fg-pick-slips');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextItem = async () => {
    try {
      // Use query parameter endpoint to handle slashes in slip numbers
      const response = await apiClient.get(`/wms/fg/pick-slip-next-item?slip_number=${encodeURIComponent(slipNumber)}`);
      
      if (response.data.has_next) {
        setCurrentItem(response.data.item);
        setItemIndex(response.data.item.index);
        setBins(response.data.bins || []);
        setTotalAvailable(response.data.total_available);
        setProgress(response.data.progress);
        setTimeout(() => scanInputRef.current?.focus(), 100);
      } else {
        setCurrentItem(null);
        setCompleted(true);
        setProgress({ completed_items: response.data.progress?.total_items || 0, total_items: response.data.progress?.total_items || 0, percentage: 100 });
      }
    } catch (error) {
      toast.error('Failed to get next item');
    }
  };

  const handleStaffValidate = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter Staff ID');
      return;
    }

    try {
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffValidated(true);
        setStaffName(response.data.staff_name);
        
        // Start the pick slip if imported - use query parameter endpoint
        if (slip.status === 'IMPORTED') {
          await apiClient.post(`/wms/fg/pick-slips/start-by-number?slip_number=${encodeURIComponent(slipNumber)}&staff_id=${staffId}`);
          toast.success('Pick slip started');
        }
        
        fetchNextItem();
      } else {
        toast.error('Invalid Staff ID');
      }
    } catch (error) {
      toast.error('Staff validation failed');
    }
  };

  const handlePackScan = async () => {
    if (!scannedCode.trim()) {
      toast.error('Please scan pack barcode');
      return;
    }

    try {
      // Use query parameter endpoint to handle slashes in slip numbers
      const response = await apiClient.post(
        `/wms/fg/pick-slip-pick-pack?slip_number=${encodeURIComponent(slipNumber)}&pack_barcode=${scannedCode}&item_index=${itemIndex}`
      );
      
      toast.success(response.data.message);
      setScannedCode('');
      
      // Update current item
      setCurrentItem(prev => ({
        ...prev,
        picked_qty: response.data.new_picked_qty,
        remaining_qty: response.data.remaining_qty
      }));
      
      // Check if slip is complete
      if (response.data.slip_complete) {
        toast.success('All items picked! Slip completed.');
        setCompleted(true);
        setCurrentItem(null);
        return;
      }
      
      // PERF: use inline next-item from pick-pack (one round-trip). Fall back
      // to a dedicated fetch if absent.
      const inlineNext = response.data.next;
      if (inlineNext !== undefined && inlineNext !== null) {
        if (inlineNext.has_next) {
          if (response.data.item_complete) {
            toast.success('Item completed! Moving to next...');
            setCurrentItem(inlineNext.item);
          }
          setBins(inlineNext.bins || []);
          setTotalAvailable(inlineNext.total_available);
          scanInputRef.current?.focus();
        } else {
          toast.success('All items picked! Slip completed.');
          setCompleted(true);
          setCurrentItem(null);
          return;
        }
      } else if (response.data.item_complete) {
        toast.success('Item completed! Moving to next...');
        fetchNextItem();
      } else {
        // Refresh bins using query parameter endpoint
        const nextResponse = await apiClient.get(`/wms/fg/pick-slip-next-item?slip_number=${encodeURIComponent(slipNumber)}`);
        if (nextResponse.data.has_next) {
          setBins(nextResponse.data.bins || []);
          setTotalAvailable(nextResponse.data.total_available);
        }
        scanInputRef.current?.focus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pick pack');
      setScannedCode('');
      scanInputRef.current?.focus();
    }
  };

  const handleSkipItem = async () => {
    if (!skipRemarks.trim()) {
      toast.error('Remarks are required for partial picking');
      return;
    }

    try {
      // Use query parameter endpoint to handle slashes in slip numbers
      const response = await apiClient.post(
        `/wms/fg/pick-slip-complete-item?slip_number=${encodeURIComponent(slipNumber)}&item_index=${itemIndex}&remarks=${encodeURIComponent(skipRemarks)}`
      );
      
      toast.success('Item marked as complete (partial)');
      setSkipDialogOpen(false);
      setSkipRemarks('');
      
      if (response.data.slip_completed) {
        setCompleted(true);
        setCurrentItem(null);
      } else {
        fetchNextItem();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to skip item');
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <p className="text-[#5A5F6E]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg-pick-slips')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">FG Picker Guidance</h1>
              <p className="text-sm text-[#E8DCC8]">Slip: {slipNumber}</p>
            </div>
          </div>
          {staffValidated && (
            <div className="text-right">
              <p className="text-sm text-[#E8DCC8]">Picker</p>
              <p className="font-medium">{staffName}</p>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 md:px-6 py-8">
        {/* Progress Bar */}
        {staffValidated && !completed && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-[#5A5F6E]">Progress</span>
              <span className="text-sm font-medium">{progress.completed_items} / {progress.total_items} items</span>
            </div>
            <Progress value={progress.percentage} className="h-3" />
          </div>
        )}

        {/* Completed Screen */}
        {completed && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1A1F2E] mb-2">Pick Slip Completed!</h2>
            <p className="text-[#5A5F6E] mb-6">
              All items have been processed.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate('/wms/fg-pick-slips')}
              >
                Back to Pick Slips
              </Button>
              <Button
                onClick={() => navigate('/wms')}
                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              >
                WMS Dashboard
              </Button>
            </div>
          </div>
        )}

        {/* Staff Validation */}
        {!staffValidated && !completed && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">Staff Validation</h2>
            <p className="text-[#5A5F6E] mb-4">
              Please scan or enter your Staff ID to begin picking.
            </p>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label>Staff ID</Label>
                <Input
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                  placeholder="Scan or enter Staff ID"
                  className="mt-1"
                  autoFocus
                  data-testid="staff-id-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleStaffValidate}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                >
                  Start Picking
                </Button>
              </div>
            </div>

            {/* Slip Summary */}
            {slip && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-[#1A1F2E] mb-2">Slip Summary</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#5A5F6E]">Total Items:</span>
                    <span className="ml-2 font-medium">{slip.total_items}</span>
                  </div>
                  <div>
                    <span className="text-[#5A5F6E]">Total Qty:</span>
                    <span className="ml-2 font-medium">{slip.total_qty?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Current Item to Pick */}
        {staffValidated && !completed && currentItem && (
          <div className="space-y-6">
            {/* Item Info */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Current Item</p>
                    <p className="text-xl font-bold font-mono text-[#1A1F2E]">{currentItem.barcode}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSkipDialogOpen(true)}
                  className="text-orange-600 border-orange-300"
                >
                  <SkipForward className="w-4 h-4 mr-1" />
                  Skip/Partial
                </Button>
              </div>
              
              {currentItem.design_name && (
                <p className="text-[#5A5F6E] mb-3">{currentItem.design_name}</p>
              )}
              
              {/* Sales Executive Remarks - Prominent Display */}
              {currentItem.remarks && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-amber-800">Remarks from Sales:</p>
                      <p className="text-amber-700">{currentItem.remarks}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-[#5A5F6E]">Required</p>
                  <p className="font-bold text-lg">{currentItem.required_qty}</p>
                </div>
                <div className="p-3 bg-green-50 rounded">
                  <p className="text-green-600">Picked</p>
                  <p className="font-bold text-lg text-green-700">{currentItem.picked_qty}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded">
                  <p className="text-blue-600">Remaining</p>
                  <p className="font-bold text-lg text-blue-700">{currentItem.remaining_qty}</p>
                </div>
              </div>
            </div>

            {/* FIFO Bin Guidance */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Go to Location (FIFO)</p>
                  {bins.length > 0 ? (
                    <p className="text-2xl font-bold text-[#1A1F2E]">{bins[0].bin_code}</p>
                  ) : (
                    <p className="text-lg text-red-600">No stock available</p>
                  )}
                </div>
              </div>
              
              {bins.length > 0 && (
                <div className="text-sm text-blue-700">
                  <p>Available in this bin: <strong>{bins[0].available_qty}</strong> packs</p>
                  <p className="mt-1">Total available across all bins: <strong>{totalAvailable}</strong> packs</p>
                </div>
              )}

              {bins.length > 1 && (
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <p className="text-sm text-blue-600 mb-2">Next bins (FIFO order):</p>
                  <div className="space-y-1">
                    {bins.slice(1, 4).map((bin, idx) => (
                      <div key={bin.bin_code} className="text-sm text-blue-700 flex justify-between">
                        <span>{bin.bin_code}</span>
                        <span>{bin.available_qty} packs</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Scan Pack */}
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Scan className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-green-600 font-medium">Scan Pack (No USN Restriction)</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    ref={scanInputRef}
                    value={scannedCode}
                    onChange={(e) => setScannedCode(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, handlePackScan)}
                    placeholder="Scan any pack barcode from this SKU"
                    className="text-lg h-12"
                    data-testid="pack-scan-input"
                  />
                </div>
                <Button
                  onClick={handlePackScan}
                  disabled={!scannedCode || bins.length === 0}
                  className="bg-green-600 hover:bg-green-700 text-white h-12 px-6"
                  data-testid="confirm-pick-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Pick
                </Button>
              </div>
              <p className="text-sm text-green-700 mt-2">
                Scan any available pack - no specific USN required
              </p>
            </div>

            {/* Stock Warning */}
            {totalAvailable < currentItem.remaining_qty && totalAvailable > 0 && (
              <div className="flex items-start gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Insufficient Stock</p>
                  <p>Only {totalAvailable} packs available. Required: {currentItem.remaining_qty}. Use "Skip/Partial" to continue with partial picking.</p>
                </div>
              </div>
            )}

            {totalAvailable === 0 && (
              <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-medium">No Stock Available</p>
                  <p>This SKU has no stock in FG storage. Use "Skip/Partial" to skip this item.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Skip/Partial Dialog */}
      <Dialog open={skipDialogOpen} onOpenChange={setSkipDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Skip Item / Partial Pick</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {currentItem && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p><strong>SKU:</strong> {currentItem.barcode}</p>
                <p><strong>Required:</strong> {currentItem.required_qty}</p>
                <p><strong>Picked:</strong> {currentItem.picked_qty}</p>
                <p><strong>Remaining:</strong> {currentItem.remaining_qty}</p>
              </div>
            )}
            <div>
              <Label>Remarks (Required)</Label>
              <Textarea
                value={skipRemarks}
                onChange={(e) => setSkipRemarks(e.target.value)}
                placeholder="Enter reason for partial/skip (e.g., 'Stock unavailable', '10 pcs short')"
                className="mt-1"
                rows={3}
                data-testid="skip-remarks-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSkipDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSkipItem}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              disabled={!skipRemarks.trim()}
            >
              Confirm Skip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FGPickerGuidance;
